import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, effect, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import {
  lucideArrowLeft,
  lucideCheck,
  lucideChevronDown,
  lucideCopy,
  lucideEllipsisVertical,
  lucidePen,
  lucidePlus,
  lucideTrash,
  lucideX,
} from '@ng-icons/lucide';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmDatePickerImports, provideHlmDatePickerConfig } from '@spartan-ng/helm/date-picker';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmLabelImports } from '@spartan-ng/helm/label';
import { HlmSwitchImports } from '@spartan-ng/helm/switch';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { DateTime } from 'luxon';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { PaginateurComponent } from '../../shared/components/paginateur/paginateur.component';
import { SidepanelService } from '../../shared/components/sidepanel/sidepanel.service';
import { CreationCartoComponent } from '../modals/creation-carto/creation-carto.component';
import { CartographieLivrable } from '../models/cartographie-livrable.model';
import { Cartographie } from '../models/cartographie.model';
import { CartographieUpdateRequest } from '../request/update-request';
import { CartographieStore } from '../store/cartographie.store';

type LivrableGroup = {
  id: FormControl<string>;
  nom: FormControl<string>;
  dateEcheance: FormControl<Date | null>;
  livrablesIntermediaires: FormArray<LivrableFormGroup>;
};

type CartographieGroup = {
  id: FormControl<string>;
  nom: FormControl<string>;
  estActive: FormControl<boolean>;
  editing: FormControl<boolean>;
  livrables: FormArray<LivrableFormGroup>;
};

type LivrableFormGroup = FormGroup<LivrableGroup>;
type CartographieFormGroup = FormGroup<CartographieGroup>;

@Component({
  selector: 'app-carto-list',
  imports: [
    PaginateurComponent,
    HlmButtonImports,
    HlmIconImports,
    HlmSwitchImports,
    HlmLabelImports,
    HlmTooltipImports,
    HlmTableImports,
    HlmCardImports,
    NgTemplateOutlet,
    HlmDropdownMenuImports,
    DatePipe,
    HlmFieldImports,
    HlmDatePickerImports,
    ReactiveFormsModule,
    HlmInputImports,
  ],
  templateUrl: './carto-list.component.html',
  providers: [
    provideIcons({
      lucideCopy,
      lucideArrowLeft,
      lucideChevronDown,
      lucidePlus,
      lucidePen,
      lucideTrash,
      lucideEllipsisVertical,
      lucideX,
      lucideCheck,
    }),
    provideHlmDatePickerConfig({
      autoCloseOnSelect: true,
      formatDate: (date: Date) => DateTime.fromJSDate(date).toFormat('dd MMM yyyy'),
    }),
  ],
})
export class CartoListComponent {
  readonly store = inject(CartographieStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #fb = inject(FormBuilder);

  readonly form: FormGroup = this.#fb.group({
    cartographies: this.#fb.array<CartographieFormGroup>([]),
  });

  isEditing = signal(false);
  expandedRows = new Map<string, boolean>();

  constructor() {
    effect(() => {
      this.store.entities();

      this.#initFormulaire();
    });
  }

  #initFormulaire() {
    const cartographies = this.store.entities();

    this.cartographiesFa.clear();
    cartographies.forEach((cartographie) => {
      this.cartographiesFa.push(this.#createCartographieFormGroup(cartographie));
    });
  }

  livrablesAt(cartographieIndex: number) {
    return this.cartographiesFa.at(cartographieIndex).controls.livrables;
  }

  livrablesIntermediairesAt(cartographieIndex: number, livrableIndex: number) {
    return this.livrablesAt(cartographieIndex).at(livrableIndex).controls.livrablesIntermediaires;
  }

  toggleCartographieActive(i: number) {
    const ctrl = this.cartographiesFa.at(i);
    this.store.modifier({
      id: ctrl.value.id!,
      data: {
        estActive: !ctrl.value.estActive,
      },
    });
  }

  toggleExpand(id?: string): void {
    if (!id) return;

    this.expandedRows.set(id, !this.expandedRows.get(id));
  }

  toggleEdit(i: number): void {
    const ctrl = this.cartographiesFa.at(i);
    ctrl.patchValue({ editing: !ctrl.value.editing });
    this.isEditing.set(true);
  }

  ajouterLivrableIntermediaire(cartoIndex: number, livrableIndex: number): void {
    const parent = this.livrablesAt(cartoIndex).at(livrableIndex);
    parent.controls.livrablesIntermediaires?.push(this.#createLivrable());
    this.expandedRows.set(parent.controls.id.value, true);
  }

  supprimerLivrableIntermediaire(cartoIndex: number, livrableIndex: number, livrableIntermediaireIndex: number): void {
    this.livrablesIntermediairesAt(cartoIndex, livrableIndex)?.removeAt(livrableIntermediaireIndex);
  }

  duplicate(i: number) {
    const idCartographie = this.cartographiesFa.at(i).value.id;
    if (!idCartographie) return;

    const cartographie = this.store.entityMap()[idCartographie];
    this.#sidepanelService.setComponentToDisplay(
      'Duplication de la cartographie',
      CreationCartoComponent,
      `La cartographie que vous êtes en train de dupliquer est la <b>${cartographie.nom}</b>.`,
      { cartographie: cartographie },
    );
    this.#sidepanelService.open();
  }

  submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      const toastrId = uuidv7();
      toast.custom(ToastrErrorComponent, {
        componentProps: {
          message: `Merci de renseigner les champs obligatoires.`,
          id: toastrId,
        },
        id: toastrId,
      });
      return;
    }

    const controls = this.cartographiesFa.controls.filter((x) => !!x.value.editing);
    const cartographiesAMettreAJour: CartographieUpdateRequest[] = [];

    for (const cartographie of controls.values()) {
      cartographiesAMettreAJour.push({
        id: cartographie.value.id,
        nom: cartographie.value.nom,
        livrables:
          cartographie.value.livrables?.map((x) => ({
            id: x.id!,
            dateEcheance: x.dateEcheance
              ? `${x.dateEcheance.getFullYear()}-${String(x.dateEcheance.getMonth() + 1).padStart(2, '0')}-${String(x.dateEcheance.getDate()).padStart(2, '0')}`
              : null,
            nom: x.nom!,
            livrablesIntermediaires:
              x.livrablesIntermediaires?.map((y) => ({
                id: !!y.id ? y.id : null,
                nom: y.nom!,
                dateEcheance: y.dateEcheance
                  ? `${y.dateEcheance.getFullYear()}-${String(y.dateEcheance.getMonth() + 1).padStart(2, '0')}-${String(y.dateEcheance.getDate()).padStart(2, '0')}`
                  : null,
              })) ?? [],
          })) ?? [],
      });
    }

    cartographiesAMettreAJour.forEach((cartographie) =>
      this.store.modifier({ id: cartographie.id!, data: cartographie }),
    );
    this.cancelEdit();
  }

  cancelEdit() {
    this.#initFormulaire();
    this.isEditing.set(false);
  }

  #createCartographieFormGroup(cartographie: Cartographie): CartographieFormGroup {
    return this.#fb.group({
      id: this.#fb.nonNullable.control(cartographie.id),
      nom: this.#fb.nonNullable.control(cartographie.nom, Validators.required),
      estActive: this.#fb.nonNullable.control(cartographie.estActive),
      editing: this.#fb.nonNullable.control(false),
      livrables: this.#fb.array(cartographie.livrables.map((l) => this.#createLivrable(l))),
    });
  }

  #createLivrable(livrable?: CartographieLivrable): LivrableFormGroup {
    return this.#fb.group({
      id: this.#fb.nonNullable.control(livrable?.id ?? ''),
      nom: this.#fb.nonNullable.control(livrable?.nom ?? '', Validators.required),
      dateEcheance: this.#fb.control(livrable?.dateEcheance ? new Date(livrable.dateEcheance) : null),
      livrablesIntermediaires: this.#fb.nonNullable.array<LivrableFormGroup>(
        (livrable?.livrablesIntermediaires ?? []).map((l) => this.#createLivrable(l)),
      ),
    });
  }

  isExpanded(id?: string): boolean {
    if (!id) return false;

    return !!this.expandedRows.get(id);
  }

  get cartographiesFa(): FormArray<CartographieFormGroup> {
    return this.form.controls['cartographies'] as FormArray<CartographieFormGroup>;
  }

  get nom() {
    return this.form.get('nom') as AbstractControl;
  }

  get livrables() {
    return this.form.get('livrables') as FormArray<FormGroup>;
  }
}
